import { SectionOptions } from "@legacy-components/general/modals/ReportGenerationModal";
import { SectionFile } from "@legacy-components/MedicalRecordFilesSection";
import { addMinutes } from "date-fns";
import {
  ADLFieldSectionMapping,
  ParticipantType as APIParticipantType,
  AppointmentRecord,
  AppointmentRecordPatientCase,
  AppointmentStatus,
  AppointmentType,
  BackDoorStatus,
  BodyPart,
  DiagnosisMapping,
  DiagnosticStudySource,
  DiagnosticStudyType,
  Doctor,
  DoctorStatus,
  EmploymentType,
  FieldTemplate,
  FileType,
  FlexibleDate,
  FlexibleDateInput,
  FlexibleTimeWindow,
  InquiringParty,
  Language,
  Location,
  NoShowReason,
  Notes,
  ParticipantEmailStatuses,
  Patient,
  ReportSource,
  ServiceDetail,
  ServiceModifier,
  ServiceType,
  SpecialtyType,
  WorkflowExecutionStatus,
} from "~/api/API";
import type { components } from "~/api/generated/api-server-types";
import { toRawICD10Code } from "~/common/icd10";
import {
  isSupportedReportGenerationAppointmentType,
  SupportedReportGenerationAppointmentType,
} from "~/common/types";
import { DEFAULT_DATE_ISO, DEFAULT_TIME_ISO } from "~/constants";
import {
  AppointmentFormData,
  BillingFormData,
  ClaimInfoFormData,
  PatientFormData,
  ServiceDetailFormData,
} from "~/pages/appointments/types";
import { DocumentType } from "~/pages/documents/BaseDocumentPage";
import { LocationFormData } from "~/pages/locations/type";
import {
  FlexibleTimeWindowFormData,
  PhysicianFormData,
  PhysicianPreferencesFormData,
} from "~/pages/physicians/type";
import {
  DiagnosisPointer,
  ServiceDetail as LambdaServiceDetail,
  ParticipantPartyInfo,
  Qme110FormData,
  ReportType,
} from "~/pydantic_models/lambdas/models/documents";
import { ParticipantType as LambdaParticipantType } from "~/pydantic_models/lambdas/models/emails";
import { DateISO } from "./DateISO";
import { coalesce, makeFlexibleDate } from "./PartialDate";
import { sortPatientCases } from "./sortings";
import {
  areAllObjectFieldsEmpty,
  getFutureDateFromDateISO,
  getTodayDateISOStr,
  isNoInterpreterNeeded,
  normalizeNullableId,
} from "./utils";

type WorkflowStatus =
  components["schemas"]["WorkflowCheckStatusResponse"]["status"];
type ReportUISelectedSource = components["schemas"]["ReportUISelectedSource"];

interface Option<T> {
  label: string;
  value: T;
}

/** Keep the recalled date independent of the exact panel/demo date in forms. */
const injuryFormDate = (
  flexible: FlexibleDate | null | undefined,
): FlexibleDateInput | null =>
  makeFlexibleDate(flexible?.value ?? null, flexible?.precision);

export function stringEnumToArray(
  enumObj: Record<string, string>,
): { label: string; value: string }[] {
  return Object.keys(enumObj).map((key) => ({
    label: key,
    value: enumObj[key],
  }));
}

export const toTitleCase = (str: string) => {
  return str
    .toLowerCase()
    .replaceAll("_", " ")
    .replace(/(^\w|\s\w)/g, (m) => m.toUpperCase());
};

export const toTitleCaseTrimSpaces = (str: string): string => {
  return str
    .split(/[_\s]+/) // Split by underscores or spaces
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const toSentenceCase = (str: string): string => {
  const cleaned = str.replace(/[_\s]+/g, " ").toLowerCase();
  if (cleaned.length === 0) return "";
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
};

export const formatLanguage = (language: Language): string => {
  return language === Language.ASL ? "ASL" : toTitleCase(language);
};

export const toLowerCase = (str: string): string => {
  return str.replace(/[_\s]+/g, " ").toLowerCase();
};

export const toSentenceCaseTrimSpaces = (str: string): string => {
  const trimmed = str
    .trim()
    .replace(/[_\s]+/g, " ")
    .toLowerCase();
  if (trimmed.length === 0) return "";
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
};

// Note that this only transform the label, not the value string. To match the value options in LabelValueBlock, use CAPITAL_CASE
export const enumToOptions = <T extends string>(
  enumObject: Record<string, T> | Record<number, T>,
  labelTransform: (label: T) => string = (label: T) => label,
): Option<T>[] => {
  // Get the string values of the enum, filtering out potential reverse number mappings
  const enumValues = Object.values(enumObject).filter(
    (v): v is T => typeof v === "string",
  );

  return enumValues.map((value) => ({
    value: value,
    label: labelTransform(value),
  }));
};

export const uploadedFileTypeToDisplayName: Record<FileType, string> = {
  [FileType.QME_NOTIFICATION_FORM]: "Notice 110",
  [FileType.PANEL_LIST]: "Panel List",
  [FileType.COVER_LETTER]: "Cover Letter",
  [FileType.COVER_LETTER_MISC]: "Cover Letter Misc",
  [FileType.COVER_LETTER_RAW]: "Cover Letter Raw",
  [FileType.DECLARATION]: "Declaration",
  [FileType.MEDICAL_RECORD]: "Original Medical Records",
  [FileType.DICTATION]: "Dictation",
  [FileType.DICTATION_TRANSCRIPT]: "Dictation Transcript",
  [FileType.HISTORY_RECORDING]: "History Recording",
  [FileType.HISTORY_TRANSCRIPT]: "History Transcript",
  [FileType.PATIENT_VISIT_FORM]: "Patient Visit Form",
  [FileType.SUB_ROSA]: "Sub Rosa",
  [FileType.UNDETERMINED]: "Undetermined",
  [FileType.CASE_INTAKE]: "Case Intake",
  [FileType.BILLING_RECEIPT]: "Billing Receipt",
  [FileType.DENIED_BILL]: "Denied Bill",
  [FileType.APPROVED_BILL]: "Approved Bill",
  [FileType.DEMOGRAPHICS]: "Demographics",
  [FileType.FINAL_REPORT]: "Final Report",
  [FileType.DEU_100]: "DEU 100",
  [FileType.DEU_101]: "DEU 101",
  [FileType.PRESCRIPTION]: "Prescription",
  [FileType.APPOINTMENT_NOTICE]: "Appointment Notice",
  [FileType.RTW_VOUCHER]: "Return to Work Voucher",
  [FileType.REPORT_PACKAGE]: "Report Package",
  [FileType.AUTHORIZATION_LETTER]: "Authorization Letter",
  [FileType.JOB_DESCRIPTION]: "Job Description",
  [FileType.LATE_CANCEL_NOTICE]: "Late Cancel Notice",
  [FileType.LATE_RESCHEDULE_NOTICE]: "Late Reschedule Notice",
  [FileType.DEPOSITION_TRANSCRIPT]: "Deposition Transcript",
  [FileType.PREVIOUS_REPORTS]: "Previous Reports",
};

export const DiagnosticStudyTypeToLabel: {
  [key in DiagnosticStudyType]: string;
} = {
  [DiagnosticStudyType.X_RAY]: "X-ray",
  [DiagnosticStudyType.MRI]: "MRI",
  [DiagnosticStudyType.CT]: "CT",
  [DiagnosticStudyType.EMG_NCS]: "EMG/NCS",
  [DiagnosticStudyType.ELECTRODIAGNOSTIC_STUDY]: "Electrodiagnostic Study",
  [DiagnosticStudyType.OTHER]: "Other",
};

export const DiagnosticStudySourceToLabel: {
  [key in DiagnosticStudySource]: string;
} = {
  [DiagnosticStudySource.ACTUAL_STUDY]: "Actual Study",
  [DiagnosticStudySource.FROM_NOTE]: "From Note",
  [DiagnosticStudySource.OTHER]: "Other/Unspecified Source",
};

/**
 * Billing related mappings and converters
 */

/**
 * Maps an appointment to a report type
 * @param appointment - The appointment to map
 * @returns The report type
 */
export const mapAppointmentToReportType = (
  appointment: AppointmentRecord,
): ReportType => {
  if (
    appointment.status === AppointmentStatus.NO_SHOW ||
    appointment.status === AppointmentStatus.CANCELLED ||
    appointment.status === AppointmentStatus.LATE_CANCELLED ||
    appointment.status === AppointmentStatus.RESCHEDULED ||
    appointment.status === AppointmentStatus.LATE_RESCHEDULED
  ) {
    return "MISSED_APPOINTMENT";
  }
  if (!appointment.type) {
    alert("Appointment type is not set");
    throw new Error("Appointment type is not set");
  }
  switch (appointment.type) {
    case AppointmentType.AME:
    case AppointmentType.QME:
    case AppointmentType.SIBTF:
    case AppointmentType.IME:
    case AppointmentType.IME_ADR:
    case AppointmentType.BRIEFING: // TODO(jasonduochen): check with product to see if we need to exclude briefing from billing logic
      return "INITIAL_ASSESSMENT";
    case AppointmentType.QME_REEVALUATION:
    case AppointmentType.AME_REEVALUATION:
    case AppointmentType.SIBTF_REEVALUATION:
    case AppointmentType.IME_REEVALUATION:
    case AppointmentType.IME_ADR_REEVALUATION:
    case AppointmentType.SUPPLEMENTAL:
    case AppointmentType.RECORD_REVIEW:
      return "PROGRESS_REPORT";
    case AppointmentType.DEPOSITION:
      return "DEPOSITION";
  }
};

/**
 * Mapping of procedure codes to their respective charges
 */
export const procedureChargesMapping: Record<ServiceType, number> = {
  [ServiceType.ML200]: 503.75, // Missed Appointment
  [ServiceType.ML201]: 2015, // Comprehensive Medical-Legal Evaluation
  [ServiceType.ML202]: 1316.25, // Follow-up Medical-Legal Evaluation
  [ServiceType.ML203]: 650, // Supplemental
  [ServiceType.ML205]: 325 / 4, // Sub Rosa (per 0.25 hour of video, $325 per hour)
  [ServiceType.MLPRR]: 3, // Pages (per page)
  [ServiceType.ML204]: 455 / 4, // Deposition (per 0.25 hour; 2 hours minimum)
};

/**
 * Mapping of procedure codes to their respective units
 */
export const procedureUnitsMapping: Record<ServiceType, string | null> = {
  [ServiceType.ML200]: null,
  [ServiceType.ML201]: null,
  [ServiceType.ML202]: null,
  [ServiceType.ML203]: null,
  [ServiceType.ML205]: "1/4 hours",
  [ServiceType.MLPRR]: "pages",
  [ServiceType.ML204]: "1/4 hours",
};

/**
 * Mapping of ServiceModifier enum values to their corresponding modifier codes
 */
export const modifierMapping: Record<
  ServiceModifier,
  "93" | "94" | "95" | "96"
> = {
  [ServiceModifier.QME]: "95",
  [ServiceModifier.INTERPRETER]: "93",
  [ServiceModifier.AME]: "94",
  [ServiceModifier.PSYCH]: "96",
};

/**
 * Generates a physician signature
 * @param name - The name of the physician
 * @param title - The title of the physician
 * @returns The physician signature
 */
export const generatePhyisicianSignature = (
  name: string,
  title: string | null | undefined,
) => {
  return `${name}${title ? `, ${title}` : ""}`;
};

export const convertTo24HourFormat = (time12h: string): string =>
  time12h.replace(
    /(\d{2}):(\d{2})\s*(AM|PM)/,
    (_, hours: string, minutes: string, period: string) => {
      const hourNum =
        period === "PM" && hours !== "12"
          ? +hours + 12
          : period === "AM" && hours === "12"
            ? 0
            : +hours;
      return `${String(hourNum).padStart(2, "0")}:${minutes}`;
    },
  );

// Parse a date-only string as local midnight so the calendar date never shifts
// across timezones.
const parseDateOnlyAsLocal = (dateString: string) => {
  const dateStringWithoutZ = dateString.endsWith("Z") // Handle UTC date string
    ? dateString.slice(0, -1)
    : dateString;
  return new Date(dateStringWithoutZ + "T00:00:00");
};

export const formatDate = (dateString: string) =>
  parseDateOnlyAsLocal(dateString).toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });

/**
 * MM/DD from a date-only string — `formatDate` without the year, for places
 * where the year is implied by context and there is no room for it (a tooltip,
 * a dense cell). Goes through the same `parseDateOnlyAsLocal` so a plain
 * `YYYY-MM-DD` value does not slip a day west of UTC.
 */
export const formatMonthDay = (dateString: string) =>
  parseDateOnlyAsLocal(dateString).toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
  });

/** Locale + Intl options for rendering a date-only string. */
export interface DateOnlyFormat {
  locale: string;
  options: Intl.DateTimeFormatOptions;
}

// formatDate with the day of the week — for copy the MA reads back to the
// patient, who commonly asks which weekday the appointment falls on. The format
// is a parameter rather than a branch here: locales disagree on numeric date
// order (es renders DD/MM/YYYY, the transpose of en-US MM/DD/YYYY), so each
// language pins its own locale + options next to its copy.
export const formatDateWithWeekday = (
  dateString: string,
  { locale, options }: DateOnlyFormat,
) => parseDateOnlyAsLocal(dateString).toLocaleDateString(locale, options);

// Format 24-hour time string to 12-hour time string
export const formatTime = (timeString: string) => {
  const timeStringWithoutZ = timeString.endsWith("Z")
    ? timeString.slice(0, -1)
    : timeString;
  const time = new Date(`2000-01-01T${timeStringWithoutZ}`);
  return time.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

// Calculate the 24-hour end time string based on the start time and duration
export const calculateEndTime = (
  startTimeStr: string,
  durationMinutes: number,
): string => {
  // Parse the start time string
  const [startHours, startMinutes] = startTimeStr.split(":").map(Number);

  // Create a Date object with the parsed hours and minutes
  const startTime = new Date();
  startTime.setHours(startHours, startMinutes, 0, 0);

  // Add the duration in minutes
  const endTime = new Date(startTime.getTime() + durationMinutes * 60000);

  // Format the end time back into a 24-hour time string
  const endHours = String(endTime.getHours()).padStart(2, "0");
  const endMinutes = String(endTime.getMinutes()).padStart(2, "0");

  return `${endHours}:${endMinutes}`;
};

export const convertFormattedDateToISO = (formattedDate: string) => {
  const [month, day, year] = formattedDate.split("/");
  return `${year}-${month}-${day}`;
};

export const convertDateTimeToISO = (isoDate: string, time: string) => {
  return `${isoDate}T${time}`;
};

export const formatDateTime = (dateString: string, timeString: string) => {
  const date = new Date(`${dateString}T${timeString || "00:00"}`);
  const formattedDate = date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });

  const formattedTime = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return `${formattedDate} ${formattedTime}`;
};

export const formatSsn = (ssn: string | null | undefined) => {
  if (!ssn) {
    return "";
  }

  const cleaned = `${ssn}`.replace(/\D/g, "");
  const nineDigitMatch = cleaned.match(/^(\d{3})(\d{2})(\d{4})$/);
  if (nineDigitMatch) {
    return `${nineDigitMatch[1]}-${nineDigitMatch[2]}-${nineDigitMatch[3]}`;
  }
  // If the SSN is less than 9 digits, push the digits to the right and fill the rest with 'x', e.g. 123 -> xxx-xx-x123, 123456 -> xxx-12-3456
  return cleaned.padStart(9, "x").replace(/^(.{3})(.{2})(.{4})$/, "$1-$2-$3");
};

export const formatStreetAddress = (address: string): string => {
  const cleaned = address.trim();

  // Regular expression to match PO Box patterns at the start of the address
  const poBoxRegex = /^(p\.?\s*o\.?\s*box|post\s*office\s*box)\s*(.*)$/i;

  const match = cleaned.match(poBoxRegex);

  if (match) {
    // Standardized "P.O. Box" string
    const standardizedPOBox = "P.O. Box";

    // Capture the remaining part of the address after the PO Box
    const remainingAddress = match[2] ? toTitleCaseTrimSpaces(match[2]) : "";

    // Combine the standardized PO Box with the formatted remaining address
    return remainingAddress
      ? `${standardizedPOBox} ${remainingAddress}`
      : standardizedPOBox;
  }

  // If not a PO Box, format the entire address to sentence case
  return toTitleCaseTrimSpaces(cleaned);
};

export const parseEmail = (email: string): string => {
  return email.trim().toLowerCase();
};

export const parsePhoneNumber = (phoneNumber: string) => {
  // Remove all non-digit characters
  let cleaned = phoneNumber.replace(/\D/g, "");

  // Remove leading numbers if there are more than 10 digits
  if (cleaned.length > 10) {
    cleaned = cleaned.slice(cleaned.length - 10);
  }

  return cleaned;
};

export const parseSsn = (ssn: string | null | undefined) => {
  if (!ssn) {
    return "";
  }

  // Remove all non-digit characters
  let cleaned = ssn.replace(/\D/g, "");

  // Remove trailing numbers if there are more than 9 digits
  if (cleaned.length > 9) {
    cleaned = cleaned.slice(0, 9);
  }

  return cleaned;
};

export const apiToLambdaParticipantType = (
  participantType: APIParticipantType,
): LambdaParticipantType => {
  const mapping: Record<APIParticipantType, LambdaParticipantType> = {
    [APIParticipantType.PATIENT]: "PATIENT",
    [APIParticipantType.ADJUSTER]: "ADJUSTER",
    [APIParticipantType.APPLICANT_ATTORNEY]: "APPLICANT_ATTORNEY",
    [APIParticipantType.DEFENSE_ATTORNEY]: "DEFENSE_ATTORNEY",
  };
  return mapping[participantType];
};

export const lambdaToApiParticipantType = (
  participantType: LambdaParticipantType,
): APIParticipantType | undefined => {
  const mapping: Partial<Record<LambdaParticipantType, APIParticipantType>> = {
    PATIENT: APIParticipantType.PATIENT,
    ADJUSTER: APIParticipantType.ADJUSTER,
    APPLICANT_ATTORNEY: APIParticipantType.APPLICANT_ATTORNEY,
    DEFENSE_ATTORNEY: APIParticipantType.DEFENSE_ATTORNEY,
  };
  return mapping[participantType];
};

// Converting from GraphQL InquiringParty enum to Pydantic ParticipantType enum
export const inquiringPartyToParticipantType = (
  inquiringParty: InquiringParty,
): LambdaParticipantType => {
  const apiParticipantType = (() => {
    switch (inquiringParty) {
      case InquiringParty.PATIENT:
        return APIParticipantType.PATIENT;
      case InquiringParty.ADJUSTER:
        return APIParticipantType.ADJUSTER;
      case InquiringParty.APPLICANT_ATTORNEY:
        return APIParticipantType.APPLICANT_ATTORNEY;
      case InquiringParty.DEFENSE_ATTORNEY:
        return APIParticipantType.DEFENSE_ATTORNEY;
    }
  })();

  return apiToLambdaParticipantType(apiParticipantType);
};

export const noShowReasonToParticipantType = (
  noShowReason: NoShowReason,
): LambdaParticipantType => {
  switch (noShowReason) {
    case NoShowReason.INTERPRETER_DID_NOT_ATTEND:
      return "INTERPRETER";
    case NoShowReason.PATIENT_DID_NOT_ATTEND:
      return "PATIENT";
  }
};

export const appointmentTypeToLabel = (
  appointmentType: AppointmentType | string,
): string => {
  switch (appointmentType) {
    case AppointmentType.QME:
      return "QME";
    case AppointmentType.AME:
      return "AME";
    case AppointmentType.QME_REEVALUATION:
      return "QME Re-evaluation";
    case AppointmentType.AME_REEVALUATION:
      return "AME Re-evaluation";
    case AppointmentType.SUPPLEMENTAL:
      return "Supplemental";
    case AppointmentType.DEPOSITION:
      return "Deposition";
    case AppointmentType.BRIEFING:
      return "Briefing";
    case AppointmentType.SIBTF:
      return "SIBTF";
    case AppointmentType.SIBTF_REEVALUATION:
      return "SIBTF Re-evaluation";
    case AppointmentType.IME:
      return "IME";
    case AppointmentType.IME_REEVALUATION:
      return "IME Re-evaluation";
    case AppointmentType.IME_ADR:
      return "IME ADR";
    case AppointmentType.IME_ADR_REEVALUATION:
      return "IME ADR Re-evaluation";
    case AppointmentType.RECORD_REVIEW:
      return "Record Review";
  }
  return "";
};

export const calcultateCollection = (
  amountPaid: number | null | undefined,
  collectionRate: number | null | undefined,
) => {
  if (amountPaid && collectionRate) {
    return formatDollarAmount(amountPaid * collectionRate);
  }
  return null;
};

export const formatDollarAmount = (amount: number | null | undefined) => {
  if (amount !== null && amount !== undefined) {
    return Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`;
  }
  return null;
};

export const formatDateStringToISO = (dateString: string) => {
  return new Date(dateString).toISOString().split("T")[0];
};

export const convertISOToLocalDateObject = (isoDate: string) => {
  return new Date(isoDate + "T00:00:00");
};

export const formatISODate = (dateString: string) => {
  // ISO date without the time component would be parsed as UTC date
  // by Date constructor, causing formatting to produce incorrect date
  return convertISOToLocalDateObject(dateString).toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

/**
 * Gets the UTC-based day of week index (0 = Sunday) for a given ISO date string.
 * Note: This uses getUTCDay() rather than getDay() to ensure consistent day-of-week
 * calculations regardless of the local timezone. For example, "2024-01-01" will always
 * return the same day index even if run in different timezones.
 *
 * @param dateISO - ISO date string or DateISO object
 * @returns Day of week index (0-6, where 0 = Sunday)
 */
export const getDayOfWeek = (dateISO: string | DateISO) => {
  const date =
    dateISO instanceof DateISO
      ? new Date(dateISO.toString() + "T00:00:00Z")
      : new Date(dateISO + "T00:00:00Z");
  return date.getUTCDay();
};

export const removeHyphens = (str: string) => str.replace(/-/g, "");

export const extractDateWithDbDefault = (
  str: string | null | undefined,
  defaultValue: string = DEFAULT_DATE_ISO,
) => {
  return str === defaultValue ? null : str;
};

/**
 * The time, or null for "no time": the `00:00:00` placeholder the Portal
 * writes, or the same placeholder without seconds, as the search index stores
 * every time (HH:mm).
 */
export const extractTimeWithDbDefault = (
  str: string | null | undefined,
  defaultValue: string = DEFAULT_TIME_ISO,
) => {
  return str === defaultValue || str === defaultValue.slice(0, 5) ? null : str;
};

const formatAppointmentDateLabel = (date: Date): string =>
  date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const formatAppointmentTimeLabel = (date: Date): string => {
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  const strMinutes = minutes < 10 ? `0${minutes}` : `${minutes}`;
  return `${hours}:${strMinutes} ${ampm}`;
};

/**
 * Format an appointment's stored date/time into a display string. The date and
 * time-range are separated by a newline so callers can render them on two lines
 * (e.g. via `whitespace-pre-line`).
 *
 * - date + time → "Fri, Aug 15, 2025\n9:00 AM - 10:00 AM"
 * - date only → "Fri, Aug 15, 2025" — some types (e.g. Supplemental) store a
 *   date but no time (time stays at the DB default, so extractTimeWithDbDefault
 *   returns null); show the date alone rather than an invalid time.
 * - no real date (DB default / missing) → "-"
 */
export const formatDateTimeForDisplay = (
  date: string | null | undefined,
  time: string | null | undefined,
  durationMinutes: number,
): string => {
  const extractedDate = extractDateWithDbDefault(date);
  if (!extractedDate) return "-";

  const dateOnly = new Date(extractedDate).toISOString().split("T")[0];
  const extractedTime = extractTimeWithDbDefault(time);
  if (!extractedTime) {
    return formatAppointmentDateLabel(new Date(`${dateOnly}T00:00:00`));
  }

  const startTime = new Date(`${dateOnly}T${extractedTime}`);
  const endTime = addMinutes(startTime, durationMinutes);
  return `${formatAppointmentDateLabel(startTime)}\n${formatAppointmentTimeLabel(startTime)} - ${formatAppointmentTimeLabel(endTime)}`;
};

/**
 * Extracts the year and month in 'YYYY-MM' format from an ISO date string.
 *
 * @param dateISO - The ISO date string (e.g., '2024-07-07' or '2024-07-07Z').
 * @returns The extracted 'YYYY-MM' string.
 * @throws Will throw an error if the input is not a valid ISO date string.
 */
export const extractYearMonthFromDateISO = (dateISO: string): string => {
  // Define a regular expression to match 'YYYY-MM' or 'YYYY-MM-DD' formats, with optional 'Z'
  const isoRegex = /^(\d{4})-(\d{2})(?:-\d{2})?(?:Z)?$/;

  const match = isoRegex.exec(dateISO);

  if (match) {
    const year = match[1];
    const month = match[2];
    return `${year}-${month}`;
  } else {
    throw new Error(
      `Invalid ISO date format. Expected 'YYYY-MM', 'YYYY-MM-DD', or 'YYYY-MM-DDZ', received '${dateISO}'.`,
    );
  }
};

export const formatAppointmentTitle = (
  patientName: string | null | undefined,
  appointmentType: AppointmentType | null | undefined,
): string => {
  const name = patientName || "-";

  if (!appointmentType) {
    return name;
  }

  return `${name}: ${appointmentTypeToLabel(appointmentType)}`;
};

export const getTabTitle = (pathname: string): string => {
  const path = pathname.replace(/\/$/, "");

  const VIEW_TITLES: Record<string, string> = {
    "/active-cases": "Active cases",
    "/past-cases": "Past cases",
    "/appointments": "Appointments",
    "/upcoming-appointments": "Upcoming appointments",
    "/bookings": "Bookings",
    "/in-person-evaluations": "In-person evaluations",
    // Legacy route alias kept for backward compatibility.
    "/previsit-appointments": "In-person evaluations",
    "/active-visits": "Active visits",
    "/billing-and-collection": "Billing & collection",
    "/appointments/new": "New appointment",
    "/physicians": "Physicians",
    "/locations": "Locations",
    "/contacts": "Contacts",
    "/skills": "Skills",
    "/depositions": "Depositions",
    "/supplementals": "Supplementals",
  };

  return VIEW_TITLES[path] || "Indigo";
};

const reportSourceToReportUISelectedSource: Record<
  ReportSource,
  ReportUISelectedSource
> = {
  [ReportSource.WRITE]: "WRITE",
  [ReportSource.DICTATE]: "DICTATION",
};

/**
 * Resolves the assessment input method to a ReportUISelectedSource.
 * Fallback chain:
 * 1. User-provided options (from modal)
 * 2. Appointment-level configuration
 * 3. Doctor preferences (based on appointment type)
 * 4. Hard-coded default: WRITE, except DICTATION for a supplemental. A
 *    supplemental has no per-visit setting, and under WRITE an uploaded
 *    dictation is never processed; DICTATION with no audio yields the same
 *    bare assessment header WRITE would.
 *
 * History and physical exam are always written, so they have no source to resolve.
 */
export const getAssessmentSource = (
  appointment: AppointmentRecord,
  appointmentType: SupportedReportGenerationAppointmentType,
  options: SectionOptions | undefined,
): ReportUISelectedSource => {
  if (!isSupportedReportGenerationAppointmentType(appointmentType)) {
    throw new Error(`Unsupported appointment type: ${String(appointmentType)}`);
  }

  // Map appointment types to their corresponding doctor preference field
  // This mapping ensures exhaustiveness - if a new SupportedReportGenerationAppointmentType is added,
  // TypeScript will error if it's not included in this Record
  const appointmentTypeToPreferenceField: Record<
    SupportedReportGenerationAppointmentType,
    "evaluationReport" | "reEvaluationReport" | "supplementalReport"
  > = {
    [AppointmentType.QME]: "evaluationReport",
    [AppointmentType.AME]: "evaluationReport",
    [AppointmentType.SIBTF]: "evaluationReport",
    [AppointmentType.IME]: "evaluationReport",
    [AppointmentType.IME_ADR]: "evaluationReport",
    [AppointmentType.QME_REEVALUATION]: "reEvaluationReport",
    [AppointmentType.AME_REEVALUATION]: "reEvaluationReport",
    [AppointmentType.SIBTF_REEVALUATION]: "reEvaluationReport",
    [AppointmentType.IME_REEVALUATION]: "reEvaluationReport",
    [AppointmentType.IME_ADR_REEVALUATION]: "reEvaluationReport",
    [AppointmentType.SUPPLEMENTAL]: "supplementalReport",
  };

  // Get doctor preferences based on appointment type
  const preferenceField = appointmentTypeToPreferenceField[appointmentType];
  const doctorPreferences =
    appointment.doctor?.evaluationInputPreferences?.[preferenceField] || null;

  return (options?.assessment ||
    (appointment.reportConfiguration?.assessment &&
      reportSourceToReportUISelectedSource[
        appointment.reportConfiguration.assessment
      ]) ||
    (doctorPreferences?.assessment &&
      reportSourceToReportUISelectedSource[doctorPreferences.assessment]) ||
    (appointmentType === AppointmentType.SUPPLEMENTAL
      ? "DICTATION"
      : "WRITE")) as ReportUISelectedSource;
};

export const formatPatientNameForTitle = (
  fullName: string | null | undefined,
): string => {
  if (!fullName) return "-";

  const parts = fullName.split(" ");
  if (parts.length === 1) return parts[0];

  const firstName = parts[0];
  const lastNames = parts.slice(1);
  const lastNameInitials = lastNames.map((name) => name[0]).join("");

  return `${firstName} ${lastNameInitials}`;
};

export const getDocumentPageTitle = (
  patientName: string | null | undefined,
  documentType: DocumentType,
): string => {
  const formattedName = formatPatientNameForTitle(patientName);
  return `${formattedName} - ${documentType}`;
};

const appointmentTypeToShortTabLabel = (
  appointmentType: AppointmentType,
): string => {
  switch (appointmentType) {
    case AppointmentType.QME:
      return "QME";
    case AppointmentType.AME:
      return "AME";
    case AppointmentType.QME_REEVALUATION:
      return "QME RE-EVAL";
    case AppointmentType.AME_REEVALUATION:
      return "AME RE-EVAL";
    case AppointmentType.SUPPLEMENTAL:
      return "SUPP";
    case AppointmentType.DEPOSITION:
      return "DEPO";
    case AppointmentType.BRIEFING:
      return "BRIEF";
    case AppointmentType.SIBTF:
      return "SIBTF";
    case AppointmentType.SIBTF_REEVALUATION:
      return "SIBTF RE-EVAL";
    case AppointmentType.IME:
      return "IME";
    case AppointmentType.IME_REEVALUATION:
      return "IME RE-EVAL";
    case AppointmentType.IME_ADR:
      return "IME ADR";
    case AppointmentType.IME_ADR_REEVALUATION:
      return "IME ADR RE-EVAL";
    default:
      return appointmentTypeToLabel(appointmentType);
  }
};

export const getAppointmentPageTitle = (
  patientName: string | null | undefined,
  activeTab: string,
  appointmentType?: AppointmentType | null,
): string => {
  const formattedName = formatPatientNameForTitle(patientName);

  if (appointmentType) {
    return `${formattedName} - ${appointmentTypeToShortTabLabel(appointmentType)}`;
  }

  return `${formattedName} - ${activeTab}`;
};

export const participantToEmailStatusKey: Record<
  APIParticipantType,
  keyof ParticipantEmailStatuses
> = {
  [APIParticipantType.PATIENT]: "patient",
  [APIParticipantType.ADJUSTER]: "adjuster",
  [APIParticipantType.APPLICANT_ATTORNEY]: "applicantAttorney",
  [APIParticipantType.DEFENSE_ATTORNEY]: "defenseAttorney",
};

export const workflowStatusToWorkflowExecutionStatus = (
  workflowStatus: WorkflowStatus,
): WorkflowExecutionStatus => {
  const mapping: Record<WorkflowStatus, WorkflowExecutionStatus> = {
    ABORTED: WorkflowExecutionStatus.ABORTED,
    FAILED: WorkflowExecutionStatus.FAILED,
    PENDING_REDRIVE: WorkflowExecutionStatus.PENDING,
    RUNNING: WorkflowExecutionStatus.IN_PROGRESS,
    SUCCEEDED: WorkflowExecutionStatus.COMPLETED,
    TIMED_OUT: WorkflowExecutionStatus.FAILED,
  };
  return mapping[workflowStatus];
};

/**
 * Determines the icon type to display based on file name extension
 */
export const getFileIconType = (fileName: string): SectionFile["iconType"] => {
  const ext = fileName.split(".").pop()?.toLowerCase();
  if (!ext) return "other";

  const extensionMapping: Record<string, SectionFile["iconType"]> = {
    pdf: "pdf",
    mp4: "video",
    mov: "video",
    png: "xray",
    jpeg: "xray",
    jpg: "xray",
  };

  return extensionMapping[ext] || "other";
};

/**
 * The `<Icon name>` values this maps onto — a subset of the registry, and all
 * mono, so callers can still pass `fill`.
 */
export type FileIconName = "pdf" | "video" | "xray" | "file";

/**
 * Converts a file icon type to the actual icon name to use
 */
export const getFileIcon = (type: SectionFile["iconType"]): FileIconName => {
  switch (type) {
    case "pdf":
      return "pdf";
    case "video":
      return "video";
    case "xray":
      return "xray";
    default:
      return "file";
  }
};

/**
 * Maps an AppointmentRecord to AppointmentFormData for form prefilling.
 * Uses address-management flags to pick legacy fields vs entity-based values.
 * @param appointmentData The appointment record to map
 * @returns AppointmentFormData object with all fields filled from the appointment record
 */
export const mapAppointmentToAppointmentDetailsFormData = (
  appointmentData: AppointmentRecord,
): AppointmentFormData => {
  const pickCcEmails = (emails?: (string | null)[] | null) =>
    emails?.filter((email): email is string => Boolean(email)) ?? [];

  const appointmentDataWithSortedPatientCases = {
    ...appointmentData,
    patientCases: {
      ...appointmentData.patientCases,
      items: sortPatientCases(appointmentData),
    },
  };

  const firstCaseInfo =
    appointmentDataWithSortedPatientCases.patientCases?.items[0]?.patientCase;

  const contactUnavailability =
    appointmentDataWithSortedPatientCases.contactUnavailability;

  // Parties opted out of scheduled comms for this case (GET-3285). Each party
  // section derives its own toggle from this shared list.
  const suppressedComms = new Set(
    (firstCaseInfo?.scheduledCommsSuppressedParties ?? []).filter(
      (p): p is InquiringParty => p != null,
    ),
  );

  const insuranceCarrierAddress = firstCaseInfo?.insuranceCarrierAddress;
  const applicantLawFirmName = firstCaseInfo?.applicantAttorneyLawFirm?.name;
  const applicantLawFirmAddress = firstCaseInfo?.applicantAttorneyAddress;
  const defenseLawFirmName = firstCaseInfo?.defenseAttorneyLawFirm?.name;
  const defenseLawFirmAddress = firstCaseInfo?.defenseAttorneyAddress;

  // TODO: make more proper type assertions for Notes filter to infer not null/undefined attributes
  const formattedNotes =
    appointmentDataWithSortedPatientCases.notes?.items
      .filter((note): note is Notes => note !== null)
      .filter(
        (note) =>
          note.content !== null &&
          note.content !== undefined &&
          note.timeStamp !== null &&
          note.timeStamp !== undefined &&
          note.author !== null &&
          note.author !== undefined &&
          note.type !== null &&
          note.type !== undefined,
      )
      .map((note) => ({
        // Using ! to assert that the note is not null or undefined since we filtered out null/undefined notes
        content: note.content || "",
        dateTime: new Date(note.timeStamp!),
        author: note.author!,
        type: note.type!,
        id: note.id,
      })) || [];

  const newFormData: AppointmentFormData = {
    notes: { content: formattedNotes },
    inquirerInfo: {
      content: {
        party:
          appointmentDataWithSortedPatientCases.inquirerInfo?.inquiringParty ||
          null,
        name: appointmentDataWithSortedPatientCases.inquirerInfo?.name || null,
        phoneNumber:
          appointmentDataWithSortedPatientCases.inquirerInfo?.contact?.phone ||
          null,
        phoneNumberExtension:
          appointmentDataWithSortedPatientCases.inquirerInfo?.contact
            ?.phoneExtension || null,
        email:
          appointmentDataWithSortedPatientCases.inquirerInfo?.contact?.email ||
          null,
        date:
          DateISO.fromString(
            extractDateWithDbDefault(
              appointmentDataWithSortedPatientCases.inquiryDate,
            ) || null,
          ) || null,
        notification_110_required:
          appointmentDataWithSortedPatientCases.inquirerInfo
            ?.notification_110_required ?? null,
      },
    },
    tentativeAppointment: {
      content: {
        personToCall:
          appointmentDataWithSortedPatientCases.scheduler?.name || null,
        phoneNumber:
          appointmentDataWithSortedPatientCases.scheduler?.contact?.phone ||
          null,
        phoneNumberExtension:
          appointmentDataWithSortedPatientCases.scheduler?.contact
            ?.phoneExtension || null,
        organizationName:
          appointmentDataWithSortedPatientCases.scheduler?.organizationName ||
          null,
        email:
          appointmentDataWithSortedPatientCases.scheduler?.contact?.email ||
          null,
        preferredPhysician:
          appointmentDataWithSortedPatientCases.doctorID || null,
        preferredLocation:
          appointmentDataWithSortedPatientCases.locationID || null,
        dateTime: {
          date:
            DateISO.fromString(
              extractDateWithDbDefault(
                appointmentDataWithSortedPatientCases.date,
              ) || null,
            ) || null,
          time:
            extractTimeWithDbDefault(
              appointmentDataWithSortedPatientCases.time,
            ) || null,
        },
        additionalTimeslots:
          appointmentDataWithSortedPatientCases.additionalTimeslots
            ?.filter(
              (
                timeslot,
              ): timeslot is { date: string; time: string } & typeof timeslot =>
                Boolean(timeslot?.date && timeslot?.time),
            )
            .map((timeslot) => ({
              date: DateISO.fromString(timeslot.date || null) || null,
              time: timeslot.time || null,
              locationID: normalizeNullableId(timeslot.locationID),
            })) ?? [],
        appointmentType: appointmentDataWithSortedPatientCases.type || null,
        medicalAssistant:
          appointmentDataWithSortedPatientCases.appointmentRecordMedicalAssistantId ||
          null,
        schedulingAssistant:
          appointmentDataWithSortedPatientCases.appointmentRecordSchedulingAssistantId ||
          null,
        onSiteOp:
          appointmentDataWithSortedPatientCases.appointmentRecordOnSiteOpId ||
          null,
        reportOp:
          appointmentDataWithSortedPatientCases.appointmentRecordReportOpId ||
          null,
        billingOp:
          appointmentDataWithSortedPatientCases.appointmentRecordBillingOpId ||
          null,
        preVisitReviewer:
          appointmentDataWithSortedPatientCases.appointmentRecordPreVisitReviewerId ||
          null,
        durationMinutes:
          appointmentDataWithSortedPatientCases.durationMinutes || null,
        panelSpecialty:
          appointmentDataWithSortedPatientCases.panelSpecialty || null,
        interpreterCompanyName:
          appointmentDataWithSortedPatientCases.interpreter?.companyName ||
          null,
        interpreterPhoneNumber:
          appointmentDataWithSortedPatientCases.interpreter?.phoneNumber ||
          null,
        interpreterPhoneNumberExtension:
          appointmentDataWithSortedPatientCases.interpreter?.phoneExtension ||
          null,
        interpreterNote:
          appointmentDataWithSortedPatientCases.interpreter?.note || null,
        interpreterName:
          appointmentDataWithSortedPatientCases.interpreter?.name || null,
        interpreterCertificateID:
          appointmentDataWithSortedPatientCases.interpreter?.certificateId ||
          null,
        videoCallLink:
          appointmentDataWithSortedPatientCases.videoCallLink || null,
      },
    },
    patientInfo: {
      content: {
        patientName: firstCaseInfo?.patient?.name || null,
        dateOfBirth:
          DateISO.fromString(firstCaseInfo?.patient?.dateOfBirth || null) ||
          null,
        SSN: firstCaseInfo?.patient?.SSN || null,
        email: firstCaseInfo?.patient?.contact?.email || null,
        cellPhoneNumber: firstCaseInfo?.patient?.contact?.phone || null,
        homePhoneNumber:
          firstCaseInfo?.patient?.contact?.homePhoneNumber || null,
        homeMailingAddress: {
          street: firstCaseInfo?.patient?.contact?.address?.street || null,
          city: firstCaseInfo?.patient?.contact?.address?.city || null,
          state: firstCaseInfo?.patient?.contact?.address?.state || null,
          zipcode: firstCaseInfo?.patient?.contact?.address?.zip || null,
        },
        language: firstCaseInfo?.patient?.language || null,
        dominantHand: firstCaseInfo?.patient?.dominantHand || null,
        isEmailUnavailable: Boolean(
          contactUnavailability?.patient?.emailUnavailable,
        ),
        isPhoneUnavailable: Boolean(
          contactUnavailability?.patient?.phoneUnavailable,
        ),
        suppressScheduledComms: suppressedComms.has(InquiringParty.PATIENT),
      },
    },
    claimInfo: {
      content: appointmentDataWithSortedPatientCases.patientCases?.items
        .filter((row): row is AppointmentRecordPatientCase => row !== null)
        .map((row) => ({
          employerAtInjuryTime:
            row.patientCase.employmentAtInjury?.employerName || null,
          employerTelephoneNumber:
            row.patientCase.employmentAtInjury?.contact?.phone || null,
          employerTelephoneNumberExtension:
            row.patientCase.employmentAtInjury?.contact?.phoneExtension || null,
          jobTitle: row.patientCase.employmentAtInjury?.jobTitle || null,
          companyAddress: {
            street:
              row.patientCase.employmentAtInjury?.contact?.address?.street ||
              null,
            city:
              row.patientCase.employmentAtInjury?.contact?.address?.city ||
              null,
            state:
              row.patientCase.employmentAtInjury?.contact?.address?.state ||
              null,
            zipcode:
              row.patientCase.employmentAtInjury?.contact?.address?.zip || null,
          },
          injuryType: row?.patientCase.injuryInfo?.injuryType || null,
          dateOfInjury:
            DateISO.fromString(
              row.patientCase.injuryInfo?.dateOfInjury || null,
            ) || null,
          startDateOfInjury:
            DateISO.fromString(
              row.patientCase.injuryInfo?.startDateOfInjury || null,
            ) || null,
          dateOfInjuryFlexible: injuryFormDate(
            row.patientCase.injuryInfo?.dateOfInjuryFlexible,
          ),
          startDateOfInjuryFlexible: injuryFormDate(
            row.patientCase.injuryInfo?.startDateOfInjuryFlexible,
          ),
          panelNumber: row.patientCase.qmePanelNumber || null,
          claimNumber: row.patientCase.claimNumber || null,
          injuredBodyParts:
            row.patientCase.injuryInfo?.injuredBodyParts &&
            Array.isArray(row.patientCase.injuryInfo?.injuredBodyParts)
              ? row.patientCase.injuryInfo?.injuredBodyParts.filter(
                  (part): part is BodyPart => part !== null,
                )
              : null,
          claimedBodyParts:
            row.patientCase.injuryInfo?.claimedBodyParts &&
            Array.isArray(row.patientCase.injuryInfo?.claimedBodyParts)
              ? row.patientCase.injuryInfo?.claimedBodyParts.filter(
                  (part): part is BodyPart => part !== null,
                )
              : null,
          WCAB: row.patientCase.WCAB || null,
        })),
    },
    adjusterInfo: {
      content: {
        adjusterName: firstCaseInfo?.workersCompInsurance?.adjuster || null,
        adjusterPhoneNumber:
          firstCaseInfo?.workersCompInsurance?.contact?.phone || null,
        adjusterPhoneNumberExtension:
          firstCaseInfo?.workersCompInsurance?.contact?.phoneExtension || null,
        adjusterFaxNumber:
          firstCaseInfo?.workersCompInsurance?.contact?.fax || null,
        adjusterEmail:
          firstCaseInfo?.workersCompInsurance?.contact?.email || null,
        workCompInsuranceCarrierName:
          firstCaseInfo?.insuranceCarrier?.name || null,
        insuranceCarrierAddress: {
          street: insuranceCarrierAddress?.street || null,
          city: insuranceCarrierAddress?.city || null,
          state: insuranceCarrierAddress?.state || null,
          zipcode: insuranceCarrierAddress?.zip || null,
        },
        adjusterCcEmails: pickCcEmails(firstCaseInfo?.adjusterCcEmails),
        suppressScheduledComms: suppressedComms.has(InquiringParty.ADJUSTER),
      },
    },
    nurseCaseManagerInfo: {
      content: {
        ncmEmail: firstCaseInfo?.nurseCaseManager?.contact?.email || null,
        ncmPhoneNumber: firstCaseInfo?.nurseCaseManager?.contact?.phone || null,
        ncmPhoneNumberExtension:
          firstCaseInfo?.nurseCaseManager?.contact?.phoneExtension || null,
        ncmFax: firstCaseInfo?.nurseCaseManager?.contact?.fax || null,
      },
    },
    applicantAttorneyInfo: {
      content: {
        applicantAttorneyName: firstCaseInfo?.applicantAttorney?.name || null,
        applicantAttorneyEmail:
          firstCaseInfo?.applicantAttorney?.contact?.email || null,
        lawFirmName: applicantLawFirmName || null,
        lawFirmAddress: {
          street: applicantLawFirmAddress?.street || null,
          city: applicantLawFirmAddress?.city || null,
          state: applicantLawFirmAddress?.state || null,
          zipcode: applicantLawFirmAddress?.zip || null,
        },
        lawFirmPhoneNumber:
          firstCaseInfo?.applicantAttorney?.contact?.phone || null,
        lawFirmPhoneNumberExtension:
          firstCaseInfo?.applicantAttorney?.contact?.phoneExtension || null,
        lawFirmFaxNumber:
          firstCaseInfo?.applicantAttorney?.contact?.fax || null,
        applicantAttorneyCcEmails: pickCcEmails(
          firstCaseInfo?.applicantAttorneyCcEmails,
        ),
        isNotPresent: Boolean(
          appointmentDataWithSortedPatientCases.applicantAttorneyNotPresent,
        ),
        isPhoneUnavailable: Boolean(
          contactUnavailability?.applicantAttorney?.phoneUnavailable,
        ),
        isEmailUnavailable: Boolean(
          contactUnavailability?.applicantAttorney?.emailUnavailable,
        ),
        suppressScheduledComms: suppressedComms.has(
          InquiringParty.APPLICANT_ATTORNEY,
        ),
      },
    },
    defenseAttorneyInfo: {
      content: {
        defenseAttorneyName: firstCaseInfo?.defenseAttorney?.name || null,
        defenseAttorneyEmail:
          firstCaseInfo?.defenseAttorney?.contact?.email || null,
        lawFirmName: defenseLawFirmName || null,
        lawFirmAddress: {
          street: defenseLawFirmAddress?.street || null,
          city: defenseLawFirmAddress?.city || null,
          state: defenseLawFirmAddress?.state || null,
          zipcode: defenseLawFirmAddress?.zip || null,
        },
        lawFirmPhoneNumber:
          firstCaseInfo?.defenseAttorney?.contact?.phone || null,
        lawFirmPhoneNumberExtension:
          firstCaseInfo?.defenseAttorney?.contact?.phoneExtension || null,
        lawFirmFaxNumber: firstCaseInfo?.defenseAttorney?.contact?.fax || null,
        defenseAttorneyCcEmails: pickCcEmails(
          firstCaseInfo?.defenseAttorneyCcEmails,
        ),
        isNotPresent: Boolean(
          appointmentDataWithSortedPatientCases.defenseAttorneyNotPresent,
        ),
        isPhoneUnavailable: Boolean(
          contactUnavailability?.defenseAttorney?.phoneUnavailable,
        ),
        isEmailUnavailable: Boolean(
          contactUnavailability?.defenseAttorney?.emailUnavailable,
        ),
        suppressScheduledComms: suppressedComms.has(
          InquiringParty.DEFENSE_ATTORNEY,
        ),
      },
    },
    carbonCopyInfo: {
      content: {
        ccName: firstCaseInfo?.ccPerson?.name || null,
        ccEmail: firstCaseInfo?.ccPerson?.contact?.email || null,
        ccAddress: {
          street: firstCaseInfo?.ccPerson?.contact?.address?.street || null,
          city: firstCaseInfo?.ccPerson?.contact?.address?.city || null,
          state: firstCaseInfo?.ccPerson?.contact?.address?.state || null,
          zipcode: firstCaseInfo?.ccPerson?.contact?.address?.zip || null,
        },
        ccPhoneNumber: firstCaseInfo?.ccPerson?.contact?.phone || null,
        ccPhoneNumberExtension:
          firstCaseInfo?.ccPerson?.contact?.phoneExtension || null,
      },
    },
  };
  return newFormData;
};

/**
 * Maps an Physician to PhysicianFormData for form prefilling
 * @param physicianData The Physician to map
 * @returns PhysicianFormData object with all fields filled from the Physician info
 */

// Physician onboarding lifecycle status labels (hyphens/spaces aren't valid in
// GraphQL enum values, so map the enum keys to their user-facing labels).
export const doctorStatusToDisplayName: Record<DoctorStatus, string> = {
  [DoctorStatus.PRE_ACTIVATING]: "Pre-Activating",
  [DoctorStatus.ACTIVATING]: "Activating",
  [DoctorStatus.ACTIVE]: "Active",
  [DoctorStatus.INACTIVE]: "Inactive",
};

// Maps a physician lifecycle status to a StatusChip color variant.
export const doctorStatusToChipVariant = (
  status: DoctorStatus,
): "todo" | "done" | "error" | "final" => {
  switch (status) {
    case DoctorStatus.ACTIVE:
      return "done";
    case DoctorStatus.INACTIVE:
      return "error";
    case DoctorStatus.ACTIVATING:
      return "final";
    default:
      return "todo";
  }
};

/**
 * Drops `__typename` so the window can be sent straight back in an
 * updateDoctor input, which rejects unknown fields.
 */
const toFlexibleTimeWindow = (
  window: FlexibleTimeWindow | null | undefined,
): FlexibleTimeWindowFormData | null =>
  window
    ? {
        startTime: window.startTime || null,
        endTime: window.endTime || null,
      }
    : null;

export const mapPhysicianToPhysicianFormData = (
  physicianData: Doctor,
): PhysicianFormData => {
  const newFormData: PhysicianFormData = {
    basicInfo: {
      content: {
        firstName: physicianData?.firstName || null,
        middleName: physicianData?.middleName || null,
        lastName: physicianData?.lastName || null,
        status: physicianData?.status ?? null,
        ramped: physicianData?.ramped ?? null,
        titleExtension: physicianData?.titleExtension || null,
        specialties:
          physicianData?.specialties?.filter(
            (specialty): specialty is SpecialtyType => specialty !== null,
          ) || null,
        email: physicianData?.contact?.email || null,
        calendarMailingList:
          physicianData?.calendarPreference?.mailingList?.filter(
            (e): e is string => Boolean(e),
          ) || null,
        officePhone: physicianData?.officePhone || null,
        cellPhone: physicianData?.cellPhone || null,
        clinicPhone: physicianData?.clinicPhone || null,
        npi: physicianData?.npi || null,
        homeCity: physicianData?.contact?.address?.city || null,
        homeState: physicianData?.homeOffice?.state || null,
        workCity: physicianData?.workCity || null,
        workState: physicianData?.workState || null,
        physicianPartnerId: physicianData?.physicianPartner?.id ?? null,
        locations:
          physicianData.locations?.items
            ?.filter(
              (item): item is NonNullable<typeof item> =>
                item !== null && item.location?.id !== undefined,
            )
            .map((item) => item.location.id) ?? [],
      },
    },
    schedulingNote: {
      content: {
        schedulingNote: physicianData.availabilitiesNote || null,
      },
    },
    generalAvailabilities: {
      content: {
        generalAvailabilities: physicianData.availabilities
          ?.generalAvailabilities
          ? physicianData.availabilities.generalAvailabilities.map(
              (availability) => ({
                during: availability?.during
                  ? {
                      start: availability.during.start || null,
                      end: availability.during.end || null,
                    }
                  : null,
                recurrence: availability?.recurrence || null,
                dayOfWeek: availability?.dayOfWeek || null,
                dayOfMonth: availability?.dayOfMonth || null,
                // Both readings of a MONTHLY rule. Copied explicitly because
                // this mapper is field-by-field: anything it forgets is not
                // just missing from the form, it is written back as null on
                // the next save.
                weekOfMonth: availability?.weekOfMonth || null,
                monthInterval: availability?.monthInterval || null,
                startTime: availability?.startTime || null,
                endTime: availability?.endTime || null,
                flexibleTimeBefore: toFlexibleTimeWindow(
                  availability?.flexibleTimeBefore,
                ),
                flexibleTimeAfter: toFlexibleTimeWindow(
                  availability?.flexibleTimeAfter,
                ),
                locationIDs: availability?.locationIDs || null,
                isAllLocations: availability?.isAllLocations ?? null,
              }),
            )
          : null,
      },
    },
    exceptionsAvailability: {
      content: {
        additionalAvailabilities: physicianData.availabilities
          ?.additionalAvailabilities
          ? physicianData.availabilities.additionalAvailabilities.map(
              (availability) => ({
                during: availability?.during
                  ? {
                      start: availability.during.start || null,
                      end: availability.during.end || null,
                    }
                  : null,
                startTime: availability?.startTime || null,
                endTime: availability?.endTime || null,
                flexibleTimeBefore: toFlexibleTimeWindow(
                  availability?.flexibleTimeBefore,
                ),
                flexibleTimeAfter: toFlexibleTimeWindow(
                  availability?.flexibleTimeAfter,
                ),
                locationIDs: availability?.locationIDs || null,
                isAllLocations: availability?.isAllLocations ?? null,
              }),
            )
          : null,
        blackoutDates: physicianData.availabilities?.blackoutDates
          ? physicianData.availabilities.blackoutDates.map((availability) => ({
              during: availability?.during
                ? {
                    start: availability.during.start || null,
                    end: availability.during.end || null,
                  }
                : null,
              startTime: availability?.startTime || null,
              endTime: availability?.endTime || null,
              allDay: availability?.allDay || null,
            }))
          : null,
      },
    },
    appointmentDuration: {
      content: {
        qme: physicianData.defaultAppointmentDurationMinutes?.qme ?? null,
        qmeReevaluation:
          physicianData.defaultAppointmentDurationMinutes?.qmeReevaluation ??
          null,
        ime: physicianData.defaultAppointmentDurationMinutes?.ime ?? null,
        deposition:
          physicianData.defaultAppointmentDurationMinutes?.deposition ?? null,
      },
    },
    earlyArrivalTime: {
      content: {
        workersComp:
          physicianData.defaultEarlyArrivalTimeMinutes?.workersComp ?? null,
        ime: physicianData.defaultEarlyArrivalTimeMinutes?.ime ?? null,
        deposition:
          physicianData.defaultEarlyArrivalTimeMinutes?.deposition ?? null,
      },
    },
    license: {
      content: {
        caLicenseNumber: physicianData?.licenseNumber || null,
        caLicenseRenewalDate:
          DateISO.fromString(physicianData?.licenseRenewalDate || null) || null,
        qmeLicenseRenewalDate:
          DateISO.fromString(physicianData?.qmeLicense?.renewalDate || null) ||
          null,
      },
    },
  };
  return newFormData;
};

/**
 * Maps physician data to preferences form data.
 *
 * @param physicianData The physician/doctor record
 * @param fieldTemplate FieldTemplate entity (required - Phase 4 removed legacy embedded fields from Doctor)
 */
export const mapPhysicianToPhysicianPreferencesFormData = (
  physicianData: Doctor,
  fieldTemplate: FieldTemplate,
): PhysicianPreferencesFormData => {
  const adlFieldSectionMappings = fieldTemplate.adlFieldSectionMappings;
  const medicalHistoryFieldIDs = fieldTemplate.medicalHistoryFieldIDs;

  const newFormData: PhysicianPreferencesFormData = {
    adlPreferences: {
      content: {
        adlFieldSectionMappings: adlFieldSectionMappings
          ? adlFieldSectionMappings
              .filter(
                (mapping): mapping is ADLFieldSectionMapping =>
                  mapping !== null,
              )
              .map((mapping: ADLFieldSectionMapping) => ({
                ADLFieldSectionID: mapping.ADLFieldSectionID || "",
                ADLFieldIDs: mapping.ADLFieldIDs || [],
              }))
          : null,
      },
    },
    reportFormatPreferences: {
      content: {
        evaluationReport:
          physicianData?.reportFormatPreferences?.evaluationReport || null,
        reEvaluationReport:
          physicianData?.reportFormatPreferences?.reEvaluationReport || null,
        supplementalReport:
          physicianData?.reportFormatPreferences?.supplementalReport || null,
      },
    },
    medicalHistoryPreferences: {
      content: {
        medicalHistoryFieldIDs: medicalHistoryFieldIDs
          ? medicalHistoryFieldIDs.filter((id): id is string => id !== null)
          : null,
      },
    },
    evaluationInputPreferences: {
      content: {
        evaluationReport: {
          assessment:
            physicianData?.evaluationInputPreferences?.evaluationReport
              .assessment || null,
        },
        reEvaluationReport: {
          assessment:
            physicianData?.evaluationInputPreferences?.reEvaluationReport
              .assessment || null,
        },
        supplementalReport: {
          assessment:
            physicianData?.evaluationInputPreferences?.supplementalReport
              .assessment || null,
        },
        preferredAssessmentMode: physicianData?.preferredAssessmentMode ?? null,
      },
    },
    assessmentTreatmentPreferences: {
      content: {
        assessmentTreatmentOptions: physicianData.assessmentTreatmentOptions
          ? physicianData.assessmentTreatmentOptions.filter(
              (option): option is NonNullable<typeof option> =>
                option !== null && option !== undefined,
            )
          : null,
      },
    },
  };
  return newFormData;
};

export const mapAppointmentToPatientFormData = (
  appointment: AppointmentRecord,
): PatientFormData => {
  // Use the same logic as Appointment Details tab for better fallback handling
  const sortedPatientCases = sortPatientCases(appointment);
  const primaryPatientCase = sortedPatientCases[0]?.patientCase;
  const patientInfo = primaryPatientCase?.patient;
  const claimsInfo: ClaimInfoFormData[] = sortedPatientCases
    .filter((row): row is AppointmentRecordPatientCase => row !== null)
    .map((row) => ({
      employerAtInjuryTime:
        row.patientCase.employmentAtInjury?.employerName || null,
      employerTelephoneNumber:
        row.patientCase.employmentAtInjury?.contact?.phone || null,
      employerTelephoneNumberExtension:
        row.patientCase.employmentAtInjury?.contact?.phoneExtension || null,
      jobTitle: row.patientCase.employmentAtInjury?.jobTitle || null,
      companyAddress: {
        street:
          row.patientCase.employmentAtInjury?.contact?.address?.street || null,
        city:
          row.patientCase.employmentAtInjury?.contact?.address?.city || null,
        state:
          row.patientCase.employmentAtInjury?.contact?.address?.state || null,
        zipcode:
          row.patientCase.employmentAtInjury?.contact?.address?.zip || null,
      },
      injuryType: row.patientCase.injuryInfo?.injuryType || null,
      dateOfInjury:
        DateISO.fromString(row.patientCase.injuryInfo?.dateOfInjury || null) ||
        null,
      startDateOfInjury:
        DateISO.fromString(
          row.patientCase.injuryInfo?.startDateOfInjury || null,
        ) || null,
      dateOfInjuryFlexible: injuryFormDate(
        row.patientCase.injuryInfo?.dateOfInjuryFlexible,
      ),
      startDateOfInjuryFlexible: injuryFormDate(
        row.patientCase.injuryInfo?.startDateOfInjuryFlexible,
      ),
      panelNumber: row.patientCase.qmePanelNumber || null,
      claimNumber: row.patientCase.claimNumber || null,
      injuredBodyParts:
        row.patientCase.injuryInfo?.injuredBodyParts &&
        Array.isArray(row.patientCase.injuryInfo?.injuredBodyParts)
          ? row.patientCase.injuryInfo?.injuredBodyParts.filter(
              (part): part is BodyPart => part !== null,
            )
          : null,
      claimedBodyParts:
        row.patientCase.injuryInfo?.claimedBodyParts &&
        Array.isArray(row.patientCase.injuryInfo?.claimedBodyParts)
          ? row.patientCase.injuryInfo?.claimedBodyParts.filter(
              (part): part is BodyPart => part !== null,
            )
          : null,
      WCAB: row.patientCase.WCAB || null,
    }));

  const employments = appointment.employments || [];

  const currentEmployment = employments.find(
    (emp) => emp?.type === EmploymentType.CURRENT,
  );
  const postInjuryEmployments = employments.filter(
    (emp) => emp?.type === EmploymentType.POST_INJURY,
  );
  const previousEmployments = employments.filter(
    (emp) => emp?.type === EmploymentType.PREVIOUS,
  );
  const employmentGaps = (appointment.employmentGaps ?? []).filter(
    (gap): gap is NonNullable<typeof gap> => gap !== null,
  );

  // The stored employment date (GET-910): the flexible pair, else the legacy
  // AWSDate wrapped as DAY for rows that predate the backfill.
  const employmentDate = (
    flexible: FlexibleDate | null | undefined,
    legacy: string | null | undefined,
  ): FlexibleDateInput | null =>
    makeFlexibleDate(
      ...coalesce(flexible?.value ?? null, flexible?.precision, legacy ?? null),
    );

  const newFormData: PatientFormData = {
    identification: {
      content: {
        name: patientInfo?.name || "",
        sex: patientInfo?.sex || null,
        occupation: patientInfo?.occupation || "",
        weight: patientInfo?.weightLb || null,
        heightFeet: patientInfo?.height?.feet || null,
        heightInches: patientInfo?.height?.inches || null,
        dominantHand: patientInfo?.dominantHand || null,
      },
    },
    claimInfo: {
      content: claimsInfo,
    },
    historyCall: {
      content: {
        dateTime: {
          date: appointment.historyCallDate
            ? DateISO.fromString(appointment.historyCallDate)
            : null,
          time: appointment.historyCallTime || null,
        },
      },
    },
    employerAtTimeOfInjury: {
      content: currentEmployment
        ? {
            employer: currentEmployment?.employerName || "",
            jobTitle: currentEmployment?.jobTitle || "",
            hoursPerWeek: currentEmployment?.hoursPerWeek || "",
            dateOfHire: employmentDate(
              currentEmployment?.dateOfHireFlexible,
              currentEmployment?.dateOfHire,
            ),
            stillWorking: currentEmployment?.stillWorkingHere ?? null,
            lastWork: employmentDate(
              currentEmployment?.dateOfTerminationFlexible,
              currentEmployment?.dateOfTermination,
            ),
            departureReason: currentEmployment?.departureReason || "",
            jobDuties: currentEmployment?.jobDuties || "",
            currentWorkRestrictions:
              currentEmployment?.currentWorkRestrictions || "",
            workStatus: currentEmployment?.workStatus || "",
            notes: currentEmployment?.notes || "",
          }
        : {
            employer: "",
            jobTitle: "",
            hoursPerWeek: "",
            dateOfHire: null,
            stillWorking: null,
            lastWork: null,
            departureReason: "",
            jobDuties: "",
            currentWorkRestrictions: "",
            workStatus: "",
            notes: "",
          },
    },
    postInjuryJobs: {
      content: postInjuryEmployments.length
        ? postInjuryEmployments.map((emp) => ({
            employer: emp?.employerName || "",
            jobTitle: emp?.jobTitle || "",
            hoursPerWeek: emp?.hoursPerWeek || "",
            dateOfHire: employmentDate(
              emp?.dateOfHireFlexible,
              emp?.dateOfHire,
            ),
            stillWorking: emp?.stillWorkingHere ?? null,
            lastWork: employmentDate(
              emp?.dateOfTerminationFlexible,
              emp?.dateOfTermination,
            ),
            jobDuties: emp?.jobDuties || "",
            currentWorkRestrictions: emp?.currentWorkRestrictions || "",
            workStatus: emp?.workStatus || "",
            notes: emp?.notes || "",
          }))
        : [
            {
              employer: "",
              jobTitle: "",
              hoursPerWeek: "",
              dateOfHire: null,
              stillWorking: null,
              lastWork: null,
              jobDuties: "",
              currentWorkRestrictions: "",
              workStatus: "",
              notes: "",
            },
          ],
    },
    previousEmployers: {
      content: previousEmployments.length
        ? previousEmployments.map((emp) => ({
            employer: emp?.employerName || "",
            jobTitle: emp?.jobTitle || "",
            hoursPerWeek: emp?.hoursPerWeek || "",
            startDate: employmentDate(emp?.dateOfHireFlexible, emp?.dateOfHire),
            endDate: employmentDate(
              emp?.dateOfTerminationFlexible,
              emp?.dateOfTermination,
            ),
            jobDuties: emp?.jobDuties || "",
            notes: emp?.notes || "",
          }))
        : [
            {
              employer: "",
              jobTitle: "",
              hoursPerWeek: "",
              startDate: null,
              endDate: null,
              jobDuties: "",
              notes: "",
            },
          ],
    },
    employmentGaps: {
      content: employmentGaps.map((gap) => ({
        type: gap.type ?? null,
        startDate: makeFlexibleDate(
          gap.startDate?.value ?? null,
          gap.startDate?.precision,
        ),
        endDate: makeFlexibleDate(
          gap.endDate?.value ?? null,
          gap.endDate?.precision,
        ),
        notes: gap.notes ?? "",
      })),
    },
    historyOfInjury: {
      content: {
        notes: appointment.historyOfInjuryNotes?.content || "",
      },
    },
    currentComplaint: {
      content: {
        notes: appointment.currentComplaintNotes?.content || "",
      },
    },
  };
  return newFormData;
};

export const mapAppointmentToBillingFormData = (
  appointment: AppointmentRecord,
): BillingFormData => {
  const serviceDetails = appointment.billingInfo?.serviceDetails?.length
    ? appointment.billingInfo?.serviceDetails
        .filter((detail): detail is ServiceDetail => detail !== null)
        .map(mapServiceDetailsToFormData)
    : [
        {
          dateFrom: null,
          dateTo: null,
          place: "",
          procedures: null,
          modifiers: [],
          diagnosisPointers: [],
          charges: 0,
          unitDays: 0,
          renderingProviderID: "",
          amountPaid: 0,
        },
      ];

  const diagnosisMappings = appointment.billingInfo?.diagnosisMappings?.length
    ? appointment.billingInfo?.diagnosisMappings
        .filter((map): map is DiagnosisMapping => map !== null)
        .filter(({ code, pointer }) => !!code && !!pointer)
        .map(({ code, pointer, description }) => {
          const rawCode = toRawICD10Code(code ?? "");
          const originalDescription =
            typeof description === "string" ? description : undefined;
          return {
            // Billing canonicalizes ICD-10 codes to raw (dot-less) form per
            // ops requirement. Normalize on read so any historical row that
            // was saved with dots displays consistently with new entries.
            code: rawCode,
            pointer: pointer ?? "",
            ...(originalDescription
              ? { description: originalDescription }
              : {}),
          };
        })
    : [];

  const billingDates = {
    paidDate: appointment.billingInfo?.paidDate
      ? extractDateWithDbDefault(appointment.billingInfo.paidDate)
        ? DateISO.fromString(appointment.billingInfo.paidDate)
        : null
      : null,
    billedDate: appointment.billingInfo?.billedDate
      ? extractDateWithDbDefault(appointment.billingInfo.billedDate)
        ? DateISO.fromString(appointment.billingInfo.billedDate)
        : null
      : null,
    correctionDate: appointment.billingInfo?.correctionDate
      ? extractDateWithDbDefault(appointment.billingInfo.correctionDate)
        ? DateISO.fromString(appointment.billingInfo.correctionDate)
        : null
      : null,
  };

  const newFormData = {
    diagnosisCodes: {
      content: diagnosisMappings,
    },
    serviceDetails: {
      content: serviceDetails,
    },
    billingDates: {
      content: billingDates,
    },
  };
  return newFormData;
};

const mapServiceDetailsToFormData = (
  details: ServiceDetail,
): ServiceDetailFormData => ({
  dateFrom: details?.dateOfStart
    ? extractDateWithDbDefault(details.dateOfStart)
      ? DateISO.fromString(details.dateOfStart)
      : null
    : null,
  dateTo: details?.dateOfEnd
    ? extractDateWithDbDefault(details.dateOfEnd)
      ? DateISO.fromString(details.dateOfEnd)
      : null
    : null,
  place: details?.place ? `${details?.place}` : "",
  procedures: details?.serviceType || null,
  modifiers: (details?.modifiers || []).filter(
    (modifier): modifier is ServiceModifier => modifier !== null,
  ),
  diagnosisPointers:
    details?.diagnosisPointers?.filter(
      (pointer): pointer is string => pointer !== null,
    ) || [],
  charges: details?.chargedAmount ? details.chargedAmount : 0,
  unitDays: details?.quantity ? details.quantity : 0,
  renderingProviderID: details?.renderingProviderId || "",
  amountPaid: details?.amountPaid ? details.amountPaid : 0,
});

export const convertFormDataServiceDetailToQueryInput = (
  service: ServiceDetailFormData,
) => ({
  dateOfStart: service.dateFrom ? service.dateFrom.toString() : null,
  dateOfEnd: service.dateTo ? service.dateTo.toString() : null,
  place: parseInt(service.place) || null,
  chargedAmount: service.charges || null,
  quantity: service.unitDays || null,
  renderingProviderId: service.renderingProviderID || null,
  modifiers: service.modifiers || [],
  diagnosisPointers: service.diagnosisPointers || [],
  serviceType: service.procedures || null,
  amountPaid: service.amountPaid || null,
});

/**
 * Maps appointment type to page deduction value for MLPRR service type
 * @param appointmentType - The appointment type
 * @returns The deduction value (number of free pages)
 */
export const getPageDeductionByAppointmentType = (
  appointmentType: AppointmentType | null | undefined,
): number => {
  if (!appointmentType) {
    // Default value for when appointment type is not specified
    // This shouldn't happen in normal flow
    return 200;
  }

  switch (appointmentType) {
    case AppointmentType.SUPPLEMENTAL:
      return 50;
    case AppointmentType.QME:
    case AppointmentType.QME_REEVALUATION:
    case AppointmentType.AME:
    case AppointmentType.AME_REEVALUATION:
    case AppointmentType.SIBTF:
    case AppointmentType.SIBTF_REEVALUATION:
    case AppointmentType.IME:
    case AppointmentType.IME_REEVALUATION:
    case AppointmentType.IME_ADR:
    case AppointmentType.IME_ADR_REEVALUATION:
      return 200;
    default:
      // For DEPOSITION, BRIEFING, or any other types
      // These shouldn't be mapped to MLPRR in the first place
      return 200;
  }
};

export const calculateAdjustedQuantity = (
  quantity: number,
  appointmentType: AppointmentType,
  procedure: ServiceType,
): number => {
  if (procedure === ServiceType.MLPRR) {
    const deduction = getPageDeductionByAppointmentType(appointmentType);
    if (quantity <= deduction) {
      return 0; // Free if within deduction limit
    }
    return quantity - deduction; // Subtract deduction pages which are free
  }
  return quantity;
};

export const convertToPdfServiceDetail = (
  service: ServiceDetailFormData,
  appointmentType: AppointmentType,
): LambdaServiceDetail => {
  if (!service.procedures) {
    throw new Error("Service type is required");
  }
  const diagnosisPointers = service.diagnosisPointers.filter(
    (pointer): pointer is DiagnosisPointer => pointer !== null,
  );

  return {
    date_of_start: service.dateFrom?.toString() || "",
    date_of_end: service.dateTo?.toString() || "",
    place_of_service: "11" as const,
    service_type: service.procedures,
    modifiers: service.modifiers.map(
      (modifier: ServiceModifier) => modifierMapping[modifier],
    ),
    diagnosis_pointers: diagnosisPointers,
    amount_charged: service.charges || 0,
    amount_paid: service.amountPaid || 0,
    quantity: calculateAdjustedQuantity(
      service.unitDays || 0,
      appointmentType,
      service.procedures,
    ),
    rendering_provider_id: service.renderingProviderID,
  };
};

export const specialtyTypeToFullName = (value: SpecialtyType): string => {
  switch (value) {
    case SpecialtyType.MAI:
      return "Allergy & Immunology";
    case SpecialtyType.MAA:
      return "Anesthesiology";
    case SpecialtyType.MDE:
      return "Dermatology";
    case SpecialtyType.MEM:
      return "Emergency Medicine";
    case SpecialtyType.MFP:
      return "Family Practice";
    case SpecialtyType.MPM:
      return "General Preventive Medicine";
    case SpecialtyType.MHH:
      return "Hand Surgery";
    case SpecialtyType.MMM:
      return "Internal Medicine";
    case SpecialtyType.MMV:
      return "Internal Medicine – Cardiovascular Disease";
    case SpecialtyType.MME:
      return "Internal Medicine – Endocrinology, Diabetes & Metabolism";
    case SpecialtyType.MMG:
      return "Internal Medicine – Gastroenterology";
    case SpecialtyType.MMH:
      return "Internal Medicine – Hematology";
    case SpecialtyType.MNB:
      return "Spine";
    case SpecialtyType.MMO:
      return "Internal Medicine – Medical Oncology";
    case SpecialtyType.MMN:
      return "Internal Medicine – Nephrology";
    case SpecialtyType.MMP:
      return "Internal Medicine – Pulmonary Disease (Pulmonology)";
    case SpecialtyType.MMR:
      return "Internal Medicine – Rheumatology";
    case SpecialtyType.MPN:
      return "Neurology";
    case SpecialtyType.MNS:
      return "Neurological Surgery";
    case SpecialtyType.MOG:
      return "Obstetrics and Gynecology";
    case SpecialtyType.MPO:
      return "Occupational Medicine";
    case SpecialtyType.MOP:
      return "Ophthalmology";
    case SpecialtyType.MOS:
      return "Orthopaedic Surgery";
    case SpecialtyType.MTO:
      return "Otolaryngology (Ear, Nose, and Throat, or ENT)";
    case SpecialtyType.MPA:
      return "Pain Medicine";
    case SpecialtyType.MHA:
      return "Pathology";
    case SpecialtyType.MPR:
      return "Physical Medicine & Rehabilitation";
    case SpecialtyType.MPD:
      return "Psychiatry";
    case SpecialtyType.MSY:
      return "Surgery";
    case SpecialtyType.MSG:
      return "Surgery – General Vascular";
    case SpecialtyType.MTS:
      return "Thoracic Surgery";
    case SpecialtyType.MTT:
      return "Medical Toxicology";
    case SpecialtyType.MUU:
      return "Urology";
    case SpecialtyType.POD:
      return "Podiatry";
    case SpecialtyType.ACA:
      return "Acupuncture";
    case SpecialtyType.DCH:
      return "Chiropractic";
    case SpecialtyType.DEN:
      return "Dentistry";
    case SpecialtyType.PHY:
      return "Psychology";
  }
};

/**
 * Physician-facing label for an appointment's QME panel specialty, e.g.
 * "Hand Surgery (MHH)". Returns `null` when no panel specialty is assigned, so
 * callers pick their own empty-state copy.
 *
 * The panel specialty — not the doctor's own specialty — defines which body
 * parts the appointment covers, so every surface that shows it uses this one
 * label to keep the wording identical across the portal.
 */
export const panelSpecialtyLabel = (
  value: SpecialtyType | null | undefined,
): string | null =>
  value ? `${specialtyTypeToFullName(value)} (${value})` : null;

/**
 * Normalize free-text specialty for matching: lowercase, fold dashes to "-",
 * "&" to "and", drop parentheticals, collapse non-alphanumerics to single
 * spaces. So "Otolaryngology (Ear, Nose...)", "Physical Medicine & Rehab" and
 * en-dash variants all reduce to a stable key.
 */
const normalizeSpecialtyText = (text: string): string =>
  text
    .toLowerCase()
    // Fold the unicode dashes (U+2012–U+2015) to hyphen-minus; a plain
    // hyphen-minus (U+002D) is already excluded from this range and falls
    // through to the [^a-z0-9] → space step below, so both spellings of
    // "Internal Medicine - Pulmonary Disease" normalize the same way.
    .replace(/[‒-―]/g, "-")
    .replace(/&/g, "and")
    .replace(/\([^)]*\)/g, "") // drop parentheticals
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/** Lookup built once from enum codes, full names, and a few panel aliases. */
const SPECIALTY_TEXT_LOOKUP: Record<string, SpecialtyType> = (() => {
  const map: Record<string, SpecialtyType> = {};
  const add = (key: string, code: SpecialtyType) => {
    const n = normalizeSpecialtyText(key);
    if (n) map[n] = code;
  };
  for (const code of Object.values(SpecialtyType)) {
    add(code, code); // e.g. "MOS"
    add(specialtyTypeToFullName(code), code); // e.g. "Orthopaedic Surgery"
  }
  // Common panel spellings that differ from the canonical full names.
  add("orthopedic surgery", SpecialtyType.MOS);
  add("ent", SpecialtyType.MTO);
  add("obgyn", SpecialtyType.MOG);
  add("pmr", SpecialtyType.MPR);
  add("physical medicine and rehab", SpecialtyType.MPR);
  return map;
})();

/**
 * Best-effort reverse of {@link specialtyTypeToFullName}: maps a free-text
 * specialty label (e.g. from a panel physician) to a {@link SpecialtyType}
 * code, or `null` when it matches no known specialty.
 */
export const specialtyTypeFromText = (
  text: string | null | undefined,
): SpecialtyType | null => {
  if (!text) return null;
  return SPECIALTY_TEXT_LOOKUP[normalizeSpecialtyText(text)] ?? null;
};

/**
 * Days added to the schedule date (reschedule date, else appointment-call date)
 * to derive the QME-110 signature / proof-of-service date, before the
 * `getQme110SignatureDate` cap.
 */
export const QME_110_SIGNATURE_DATE_OFFSET_DAYS = 2;

/**
 * QME-110 signature / proof-of-service date: schedule date + 2 days, capped at
 * today. The +2 models the mail turnaround, but the form attests to a signature
 * and a service that already happened, so a form drafted within 2 days of the
 * schedule date is dated the day it is generated rather than post-dated into
 * the future (GET-3625). Mirrors the backend `resolve_signature_date`.
 */
export const getQme110SignatureDate = (scheduleDateISO: string): string => {
  const today = getTodayDateISOStr();
  const offsetDate = getFutureDateFromDateISO(
    scheduleDateISO,
    QME_110_SIGNATURE_DATE_OFFSET_DAYS,
  );
  // ISO dates sort lexicographically, so a plain compare is a date compare.
  return offsetDate > today ? today : offsetDate;
};

/**
 * Maps appointment data into QME-110 form payload.
 */
export const mapAppointmentToQme110FormData = (
  appointment: AppointmentRecord,
  patient: Patient,
  primaryCase: AppointmentRecordPatientCase,
  rescheduledFromSource: AppointmentRecord | null = null,
): Qme110FormData => {
  const serviceParties: ParticipantPartyInfo[] = [];
  const patientCase = primaryCase.patientCase;
  const workersCompInsurance = patientCase.workersCompInsurance;

  // Patient
  if (patient) {
    serviceParties.push({
      participant_type: APIParticipantType.PATIENT,
      name: patient.name || "",
      address: {
        street: patient.contact?.address?.street || "",
        city: patient.contact?.address?.city || "",
        state: patient.contact?.address?.state || "",
        zip_code: patient.contact?.address?.zip || "",
      },
      phone_number: patient.contact?.phone
        ? {
            phone: patient.contact?.phone,
          }
        : null,
    });
  }

  // Adjuster
  if (workersCompInsurance) {
    serviceParties.push({
      participant_type: APIParticipantType.ADJUSTER,
      name: workersCompInsurance.adjuster || "",
      address: {
        street: patientCase.insuranceCarrierAddress?.street || "",
        city: patientCase.insuranceCarrierAddress?.city || "",
        state: patientCase.insuranceCarrierAddress?.state || "",
        zip_code: patientCase.insuranceCarrierAddress?.zip || "",
      },
      phone_number: workersCompInsurance?.contact?.phone
        ? {
            phone: workersCompInsurance?.contact?.phone,
          }
        : null,
    });
  }

  // Defense Attorney
  if (
    patientCase.defenseAttorney &&
    !areAllObjectFieldsEmpty(patientCase.defenseAttorney)
  ) {
    const defenseAttorneyAddress = patientCase.defenseAttorneyAddress;

    serviceParties.push({
      participant_type: APIParticipantType.DEFENSE_ATTORNEY,
      name: patientCase.defenseAttorney.name || "",
      address: {
        street: defenseAttorneyAddress?.street || "",
        city: defenseAttorneyAddress?.city || "",
        state: defenseAttorneyAddress?.state || "",
        zip_code: defenseAttorneyAddress?.zip || "",
      },
      phone_number: patientCase.defenseAttorney.contact?.phone
        ? {
            phone: patientCase.defenseAttorney.contact?.phone,
          }
        : null,
    });
  }

  // Applicant Attorney
  if (
    patientCase.applicantAttorney &&
    !areAllObjectFieldsEmpty(patientCase.applicantAttorney)
  ) {
    const applicantAttorneyAddress = patientCase.applicantAttorneyAddress;

    serviceParties.push({
      participant_type: APIParticipantType.APPLICANT_ATTORNEY,
      name: patientCase.applicantAttorney.name || "",
      address: {
        street: applicantAttorneyAddress?.street || "",
        city: applicantAttorneyAddress?.city || "",
        state: applicantAttorneyAddress?.state || "",
        zip_code: applicantAttorneyAddress?.zip || "",
      },
      phone_number: patientCase.applicantAttorney.contact?.phone
        ? {
            phone: patientCase.applicantAttorney.contact?.phone,
          }
        : null,
    });
  }

  const formData: Qme110FormData = {
    employee_name: patient?.name || "",
    employee_phone_number: patient?.contact?.phone
      ? {
          phone: patient?.contact?.phone,
        }
      : null,
    employee_address: {
      street: patient?.contact?.address?.street || "",
      city: patient?.contact?.address?.city || "",
      state: patient?.contact?.address?.state || "",
      zip_code: patient?.contact?.address?.zip || "",
    },
    date_of_injury: patientCase.injuryInfo?.dateOfInjury || "",
    panel_number: patientCase.qmePanelNumber || "",
    claim_number: patientCase.claimNumber,
    employer_name: patientCase.employmentAtInjury?.employerName || "",
    employer_address: {
      street: patientCase.employmentAtInjury?.contact?.address?.street || "",
      city: patientCase.employmentAtInjury?.contact?.address?.city || "",
      state: patientCase.employmentAtInjury?.contact?.address?.state || "",
      zip_code: patientCase.employmentAtInjury?.contact?.address?.zip || "",
    },
    adjuster_name: patientCase.workersCompInsurance?.adjuster || "",
    adjuster_phone_number: patientCase.workersCompInsurance?.contact?.phone
      ? {
          phone: patientCase.workersCompInsurance?.contact?.phone,
        }
      : null,
    // Insurance carrier from managed entity
    adjuster_company: patientCase.insuranceCarrier?.name || "",
    adjuster_address: {
      street: patientCase.insuranceCarrierAddress?.street || "",
      city: patientCase.insuranceCarrierAddress?.city || "",
      state: patientCase.insuranceCarrierAddress?.state || "",
      zip_code: patientCase.insuranceCarrierAddress?.zip || "",
    },
    // For a rescheduled appt the new record carries the original
    // inquiryDate; the reschedule event date lives in `reschedulingDate` on
    // the source record. "Date of appointment call" must reflect the day the
    // reschedule call was made, so prefer it (mirrors `signature_date` below).
    inquiry_date:
      rescheduledFromSource?.reschedulingDate ?? appointment.inquiryDate,
    appointment_date: appointment.date ?? "",
    appointment_time: appointment.time ?? "",
    appointment_location_address: {
      street: appointment.location?.address?.street || "",
      city: appointment.location?.address?.city || "",
      state: appointment.location?.address?.state || "",
      zip_code: appointment.location?.address?.zip || "",
    },
    interpreter_language:
      patient?.language && !isNoInterpreterNeeded(patient.language)
        ? toSentenceCase(patient.language)
        : null,
    signature_date: getQme110SignatureDate(
      rescheduledFromSource?.reschedulingDate ?? appointment.inquiryDate,
    ),
    service_parties: serviceParties,
  };

  return formData;
};

export const mapLocationToLocationFormData = (
  locationData: Location,
): LocationFormData => {
  const frontDoorLocked = locationData.isFrontDoorLocked ?? null;
  const isParkingFree = locationData.isParkingFree ?? null;
  const backDoorStatus = locationData.backDoor ?? null;

  const newFormData: LocationFormData = {
    locationInfo: {
      content: {
        name: locationData.name || null,
        abbreviation: locationData.abbreviation || null,
        region: locationData.region || null,
        status: locationData.status || null,
        address: {
          street: locationData.address?.street || null,
          city: locationData.address?.city || null,
          state: locationData.address?.state || null,
          zipcode: locationData.address?.zip || null,
        },
        county: locationData.address?.county || null,
        indigoOfficePhoneNumber: locationData.indigoOfficePhoneNumber || null,
        isWaitingArea: locationData.isWaitingArea ?? null,
        additionalRoomsNumber: locationData.additionalRoomsNumber ?? null,
        landlordContactInfo: locationData.landlordContactInfo || null,
        calendarCcEmails:
          locationData.calendarCcEmails
            ?.map((email) => email?.trim())
            .filter((email): email is string => Boolean(email)) || [],
      },
    },
    logistics: {
      content: {
        isParkingFree,
        parkingRate:
          isParkingFree === false ? (locationData.parkingRate ?? null) : null,
        isFrontDoorLocked: frontDoorLocked,
        frontDoorCode: frontDoorLocked
          ? locationData.frontDoorCode || null
          : null,
        backDoor: backDoorStatus,
        backDoorCode:
          backDoorStatus === BackDoorStatus.LOCKED
            ? locationData.backDoorCode || null
            : null,
        securityUnlockingProcess: locationData.securityUnlockingProcess || null,
        securityLockingProcess: locationData.securityLockingProcess || null,
        maleBathroomCode: locationData.maleBathroomCode || null,
        femaleBathroomCode: locationData.femaleBathroomCode || null,
        wifiName: locationData.wifiName || null,
        wifiPassword: locationData.wifiPassword || null,
        defaultOnsiteOpsId: locationData.locationDefaultOnsiteOpsId ?? null,
        secondaryOnsiteOpsId: locationData.locationSecondaryOnsiteOpsId ?? null,
      },
    },
  };

  return newFormData;
};
